// pattern: Imperative Shell
// Editor view — quiz editor for teachers to review, edit, add, delete questions

import { createElement, addListener, clearElement } from '../shell/dom.ts';
import { getQuiz, updateQuiz } from '../shell/api-client.ts';
import { navigate, routeToHash } from '../shell/router.ts';
import type { StoredQuestion } from '../core/question-model.ts';
import { renderFlashcardEditor } from './flashcard-editor-view.ts';

type EditableQuestion = {
  id: string;
  question: string;
  options: [string, string, string, string];
  correctAnswerIndex: 0 | 1 | 2 | 3;
  explanation: string;
  topic: string;
  source: 'extracted' | 'generated' | 'manual';
  figureDataUrl?: string;
};

type EditorState = {
  quizName: string;
  mode: 'structured' | 'pool' | 'flashcard';
  poolSize: number;
  questions: Array<EditableQuestion>;
  dirty: boolean;
  editingQuestionId: string | null;
};

function toMutable(q: StoredQuestion): EditableQuestion {
  return {
    id: q.id,
    question: q.question,
    options: [...q.options] as [string, string, string, string],
    correctAnswerIndex: q.correctAnswerIndex,
    explanation: q.explanation,
    topic: q.topic ?? '',
    source: q.source,
    ...(q.figureDataUrl ? { figureDataUrl: q.figureDataUrl } : {}),
  };
}

function generateId(): string {
  const chars = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const values = crypto.getRandomValues(new Uint8Array(8));
  return Array.from(values, (v) => chars[v % chars.length]).join('');
}

export function renderEditor(
  container: HTMLElement,
  dashboardId: string,
  quizId: string
): () => void {
  const cleanups: Array<() => void> = [];

  const state: EditorState = {
    quizName: '',
    mode: 'structured',
    poolSize: 10,
    questions: [],
    dirty: false,
    editingQuestionId: null,
  };

  // Show loading
  const loadingDiv = createElement('div', { className: 'text-center editor-loading' });

  const spinnerEl = createElement('div', { className: 'spinner' });
  const loadingText = createElement('p', {
    textContent: 'Loading quiz...',
    className: 'text-secondary editor-loading-text',
  });

  loadingDiv.append(spinnerEl, loadingText);
  container.appendChild(loadingDiv);

  // Fetch quiz
  void (async () => {
    const result = await getQuiz(quizId);

    if (!result.ok) {
      clearElement(loadingDiv);
      loadingDiv.className = 'card text-center';
      loadingDiv.style.marginTop = 'var(--spacing-2xl)';
      const errHeading = createElement('h2', { textContent: 'Quiz Not Found' });
      const errMsg = createElement('p', { className: 'text-secondary editor-error-msg' });
      errMsg.textContent = result.error || 'This quiz does not exist or has been removed.';
      const errLink = createElement('a', { textContent: 'Go Home', className: 'btn btn-primary' });
      errLink.setAttribute('href', '#/');
      loadingDiv.append(errHeading, errMsg, errLink);
      return;
    }

    const quiz = result.data;

    // Verify dashboardId matches
    if (quiz.dashboardId !== dashboardId) {
      clearElement(loadingDiv);
      loadingDiv.className = 'card text-center';
      loadingDiv.style.marginTop = 'var(--spacing-2xl)';
      const authHeading = createElement('h2', { textContent: 'Unauthorized' });
      const authMsg = createElement('p', { className: 'text-secondary editor-error-msg' });
      authMsg.textContent = 'Dashboard ID does not match this quiz.';
      const authLink = createElement('a', {
        textContent: 'Go Home',
        className: 'btn btn-primary',
      });
      authLink.setAttribute('href', '#/');
      loadingDiv.append(authHeading, authMsg, authLink);
      return;
    }

    // Flashcard mode — delegate to flashcard editor
    if (quiz.mode === 'flashcard') {
      clearElement(container);
      const flashcardCleanup = renderFlashcardEditor(container, dashboardId, quizId, quiz);
      cleanups.push(flashcardCleanup);
      return;
    }

    // Initialize state
    state.quizName = quiz.name;
    state.mode = quiz.mode;
    state.poolSize = quiz.poolSize ?? 10;
    state.questions = quiz.questions.map(toMutable);

    clearElement(container);
    renderContent();
  })();

  function renderContent(): void {
    // Back button
    const backBtn = createElement('button', {
      className: 'btn btn-secondary editor-back-btn',
      textContent: '\u2190 Back to Dashboard',
    });
    const backCleanup = addListener(backBtn, 'click', () => {
      if (state.dirty && !confirm('You have unsaved changes. Leave anyway?')) return;
      navigate(routeToHash({ type: 'dashboard', dashboardId }));
    });
    cleanups.push(backCleanup);
    container.appendChild(backBtn);

    // Quiz name
    const nameLabel = createElement('label', {
      textContent: 'Quiz Name',
      className: 'editor-label',
    });
    const nameInput = createElement('input', {
      className: 'input editor-input',
      type: 'text',
      value: state.quizName,
    });
    const nameCleanup = addListener(nameInput, 'input', () => {
      state.quizName = nameInput.value;
      state.dirty = true;
    });
    cleanups.push(nameCleanup);
    container.append(nameLabel, nameInput);

    // Mode toggle
    const modeSection = createElement('div', { className: 'editor-mode-section' });

    const modeLabel = createElement('span', {
      textContent: 'Mode: ',
      className: 'editor-mode-label',
    });

    const structuredRadio = createElement('input', {
      type: 'radio',
      name: 'quizMode',
      value: 'structured',
      id: 'mode-structured',
    });
    structuredRadio.checked = state.mode === 'structured';
    const structuredLabel = createElement('label', { textContent: ' All questions ' });
    structuredLabel.htmlFor = 'mode-structured';

    const poolRadio = createElement('input', {
      type: 'radio',
      name: 'quizMode',
      value: 'pool',
      id: 'mode-pool',
    });
    poolRadio.checked = state.mode === 'pool';
    const poolLabel = createElement('label', { textContent: ' Pool ' });
    poolLabel.htmlFor = 'mode-pool';

    const poolSizeInput = createElement('input', {
      type: 'number',
      className: 'input editor-pool-size-input',
      value: String(state.poolSize),
    });
    poolSizeInput.style.display = 'inline-block';
    poolSizeInput.min = '1';
    if (state.mode !== 'pool') poolSizeInput.style.display = 'none';

    const structuredCleanup = addListener(structuredRadio, 'change', () => {
      state.mode = 'structured';
      state.dirty = true;
      poolSizeInput.style.display = 'none';
    });
    cleanups.push(structuredCleanup);

    const poolCleanup = addListener(poolRadio, 'change', () => {
      state.mode = 'pool';
      state.dirty = true;
      poolSizeInput.style.display = 'inline-block';
    });
    cleanups.push(poolCleanup);

    const poolSizeCleanup = addListener(poolSizeInput, 'input', () => {
      state.poolSize = parseInt(poolSizeInput.value, 10) || 1;
      state.dirty = true;
    });
    cleanups.push(poolSizeCleanup);

    modeSection.append(
      modeLabel,
      structuredRadio,
      structuredLabel,
      poolRadio,
      poolLabel,
      poolSizeInput
    );
    container.appendChild(modeSection);

    // Questions header
    const questionsHeader = createElement('h2', { className: 'editor-questions-header' });
    container.appendChild(questionsHeader);

    // Questions container
    const questionsContainer = createElement('div', { className: 'editor-questions' });
    container.appendChild(questionsContainer);

    // Add question button
    const addBtn = createElement('button', {
      className: 'btn btn-secondary editor-add-btn',
      textContent: '+ Add Question',
    });
    const addCleanup = addListener(addBtn, 'click', () => {
      state.questions.push({
        id: generateId(),
        question: '',
        options: ['', '', '', ''],
        correctAnswerIndex: 0,
        explanation: '',
        topic: '',
        source: 'manual',
      });
      state.dirty = true;
      state.editingQuestionId = state.questions[state.questions.length - 1]!.id;
      renderQuestions();
    });
    cleanups.push(addCleanup);
    container.appendChild(addBtn);

    // Share section
    const shareSection = createElement('div', { className: 'editor-share-section' });

    const shareLabel = createElement('h3', {
      textContent: 'Share',
      className: 'editor-share-label',
    });

    const quizUrl = `${window.location.origin}${window.location.pathname}?q=${quizId}`;
    const shareLinkEl = createElement('p', {
      textContent: quizUrl,
      className: 'editor-share-link',
    });

    const copyBtn = createElement('button', {
      className: 'btn btn-secondary',
      textContent: 'Copy Link',
    });
    const copyCleanup = addListener(copyBtn, 'click', () => {
      void navigator.clipboard.writeText(quizUrl);
      copyBtn.textContent = 'Copied!';
      setTimeout(() => {
        copyBtn.textContent = 'Copy Link';
      }, 2000);
    });
    cleanups.push(copyCleanup);

    shareSection.append(shareLabel, shareLinkEl, copyBtn);
    container.appendChild(shareSection);

    // Action buttons
    const actionsDiv = createElement('div', { className: 'editor-actions' });

    const saveBtn = createElement('button', {
      className: 'btn btn-primary editor-save-btn',
      textContent: 'Save Changes',
    });

    const saveCleanup = addListener(saveBtn, 'click', () => {
      void handleSave(saveBtn);
    });
    cleanups.push(saveCleanup);

    actionsDiv.append(saveBtn);
    container.appendChild(actionsDiv);

    // Track question-specific cleanups separately to avoid unbounded growth
    let questionCleanups: Array<() => void> = [];

    // Initial render
    renderQuestions();

    // Flush question cleanups when the whole editor unmounts
    cleanups.push(() => questionCleanups.forEach((fn) => fn()));

    function renderQuestions(): void {
      // Flush previous question-card listeners
      questionCleanups.forEach((fn) => fn());
      questionCleanups = [];

      questionsHeader.textContent = `Questions (${state.questions.length})`;
      clearElement(questionsContainer);

      if (state.questions.length === 0) {
        const emptyMsg = createElement('p', {
          textContent: 'No questions yet. Add one to get started!',
          className: 'empty-state',
        });
        questionsContainer.appendChild(emptyMsg);
        return;
      }

      state.questions.forEach((q, index) => {
        const isEditing = state.editingQuestionId === q.id;
        const card = isEditing ? renderEditCard(q, index) : renderDisplayCard(q, index);
        questionsContainer.appendChild(card);
      });
    }

    function renderDisplayCard(q: EditableQuestion, index: number): HTMLElement {
      const card = createElement('div', { className: 'card question-card' });
      card.setAttribute('data-question-card', '');

      if (q.figureDataUrl) {
        const figureDiv = createElement('div', { className: 'question-figure' });
        const figureImg = createElement('img') as HTMLImageElement;
        figureImg.src = q.figureDataUrl;
        figureImg.alt = 'Question figure';
        figureDiv.appendChild(figureImg);
        card.appendChild(figureDiv);
      }

      const questionText = createElement('p', { className: 'question-display-text' });
      questionText.textContent = `${index + 1}. ${q.question || '(empty question)'}`;

      const optionsList = createElement('div', { className: 'question-options-list' });
      const letters = ['A', 'B', 'C', 'D'] as const;
      q.options.forEach((opt, i) => {
        const optEl = createElement('p', { className: 'question-option-item' });
        const isCorrect = i === q.correctAnswerIndex;
        optEl.textContent = `${isCorrect ? '\u2713 ' : '  '}${letters[i]}) ${opt}`;
        if (isCorrect) optEl.classList.add('question-option-correct');
        optionsList.appendChild(optEl);
      });

      const meta = createElement('p', { className: 'text-secondary question-meta' });
      meta.textContent = `Topic: ${q.topic || 'none'}  Source: ${q.source}`;

      const actions = createElement('div', { className: 'question-card-actions' });

      const editBtn = createElement('button', {
        className: 'btn btn-secondary question-edit-btn',
        textContent: 'Edit',
      });
      const editCleanup = addListener(editBtn, 'click', () => {
        state.editingQuestionId = q.id;
        renderQuestions();
      });
      questionCleanups.push(editCleanup);

      const delBtn = createElement('button', {
        className: 'btn btn-danger question-delete-btn',
        textContent: 'Delete',
      });
      const delCleanup = addListener(delBtn, 'click', () => {
        if (!confirm(`Delete question ${index + 1}?`)) return;
        state.questions.splice(index, 1);
        state.dirty = true;
        renderQuestions();
      });
      questionCleanups.push(delCleanup);

      actions.append(editBtn, delBtn);
      card.append(questionText, optionsList, meta, actions);
      return card;
    }

    function renderEditCard(q: EditableQuestion, index: number): HTMLElement {
      const card = createElement('div', {
        className: 'card question-card question-edit-card editing',
      });
      card.setAttribute('data-question-card', '');

      const heading = createElement('h3', {
        textContent: `Question ${index + 1}`,
        className: 'question-edit-heading',
      });

      card.appendChild(heading);

      if (q.figureDataUrl) {
        const figureDiv = createElement('div', { className: 'question-figure' });
        const figureLabel = createElement('p', {
          textContent: 'Figure (from PDF)',
          className: 'text-secondary question-figure-label',
        });
        const figureImg = createElement('img') as HTMLImageElement;
        figureImg.src = q.figureDataUrl;
        figureImg.alt = 'Question figure';
        figureDiv.append(figureLabel, figureImg);
        card.appendChild(figureDiv);
      }

      // Question text
      const qLabel = createElement('label', {
        textContent: 'Question text',
        className: 'editor-field-label',
      });
      const qInput = createElement('textarea', {
        className: 'input editor-field-input',
        value: q.question,
      });
      qInput.style.minHeight = '60px';
      qInput.style.resize = 'vertical';
      const qCleanup = addListener(qInput, 'input', () => {
        q.question = qInput.value;
        state.dirty = true;
      });
      questionCleanups.push(qCleanup);

      card.append(qLabel, qInput);

      // Options
      const letters = ['A', 'B', 'C', 'D'] as const;
      q.options.forEach((opt, i) => {
        const optRow = createElement('div', { className: 'editor-option-row' });

        const radioInput = createElement('input', {
          type: 'radio',
          name: `correct-${q.id}`,
          value: String(i),
        });
        radioInput.checked = i === q.correctAnswerIndex;
        const radioCleanup = addListener(radioInput, 'change', () => {
          q.correctAnswerIndex = i as 0 | 1 | 2 | 3;
          state.dirty = true;
        });
        questionCleanups.push(radioCleanup);

        const optLabel = createElement('span', {
          textContent: `${letters[i]})`,
          className: 'editor-option-label',
        });

        const optInput = createElement('input', {
          className: 'input editor-option-input',
          type: 'text',
          value: opt,
        });
        const optCleanup = addListener(optInput, 'input', () => {
          q.options[i] = optInput.value;
          state.dirty = true;
        });
        questionCleanups.push(optCleanup);

        optRow.append(radioInput, optLabel, optInput);
        card.appendChild(optRow);
      });

      // Explanation
      const expLabel = createElement('label', {
        textContent: 'Explanation',
        className: 'editor-field-label',
      });
      expLabel.style.marginTop = 'var(--spacing-md)';
      const expInput = createElement('textarea', {
        className: 'input editor-field-input',
        value: q.explanation,
      });
      expInput.style.minHeight = '44px';
      expInput.style.resize = 'vertical';
      const expCleanup = addListener(expInput, 'input', () => {
        q.explanation = expInput.value;
        state.dirty = true;
      });
      questionCleanups.push(expCleanup);

      // Topic
      const topicLabel = createElement('label', {
        textContent: 'Topic',
        className: 'editor-field-label',
      });
      const topicInput = createElement('input', {
        className: 'input editor-field-input',
        type: 'text',
        value: q.topic,
      });
      const topicCleanup = addListener(topicInput, 'input', () => {
        q.topic = topicInput.value;
        state.dirty = true;
      });
      questionCleanups.push(topicCleanup);

      // Done editing button
      const doneBtn = createElement('button', {
        className: 'btn btn-primary',
        textContent: 'Done Editing',
      });
      const doneCleanup = addListener(doneBtn, 'click', () => {
        state.editingQuestionId = null;
        renderQuestions();
      });
      questionCleanups.push(doneCleanup);

      card.append(expLabel, expInput, topicLabel, topicInput, doneBtn);
      return card;
    }
  }

  async function handleSave(saveBtn: HTMLButtonElement): Promise<void> {
    saveBtn.disabled = true;
    saveBtn.textContent = 'Saving...';

    const result = await updateQuiz(quizId, {
      dashboardId,
      name: state.quizName,
      mode: state.mode,
      poolSize: state.mode === 'pool' ? state.poolSize : undefined,
      questions: state.questions.map((q) => ({
        id: q.id,
        question: q.question,
        options: q.options,
        correctAnswerIndex: q.correctAnswerIndex,
        explanation: q.explanation,
        topic: q.topic || undefined,
        source: q.source,
        ...(q.figureDataUrl ? { figureDataUrl: q.figureDataUrl } : {}),
      })),
    });

    if (result.ok) {
      state.dirty = false;
      saveBtn.textContent = 'Saved!';
      setTimeout(() => {
        saveBtn.textContent = 'Save Changes';
        saveBtn.disabled = false;
      }, 2000);
    } else {
      saveBtn.textContent = 'Save Failed';
      saveBtn.disabled = false;
      setTimeout(() => {
        saveBtn.textContent = 'Save Changes';
      }, 2000);
    }
  }

  return () => cleanups.forEach((fn) => fn());
}
